/**
 * CurrentlyReading — the reader's active desk. Medium covers in a
 * horizontal row, each with title/author and a subtle progress bar.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SectionHeader } from '@/components/site/primitives';
import { fadeUp, stagger, viewport } from '@/lib/motion';
import { bookHref } from '@/lib/bookModel';
import { getBookProgress } from '@/components/bookshelf/utils/bookHelpers';
import BookCover from '@/components/books/BookCoverArt';
import EmptyState from './EmptyState';
import { PROFILE } from '@/constants/testIds';

export default function CurrentlyReading({ books }) {
  return (
    <section className="u-container mt-16 md:mt-20" data-testid={PROFILE.currentlyReading}>
      <SectionHeader eyebrow="On the nightstand" title="Currently Reading" />

      {books.length === 0 ? (
        <EmptyState type="currentlyReading" to="/app/explore" compact className="mt-2" />
      ) : (
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="mt-8 flex gap-5 overflow-x-auto pb-2 scrollbar-none sm:grid sm:grid-cols-2 sm:overflow-visible lg:grid-cols-3"
        >
          {books.map((book) => {
            const progress = getBookProgress(book);
            return (
              <motion.div key={book.id} variants={fadeUp} className="w-40 shrink-0 sm:w-auto">
                <Link to={bookHref(book.id)} className="group block">
                  <BookCover book={book} className="w-40 transition-transform duration-300 ease-soft group-hover:-translate-y-1 group-hover:shadow-lift sm:w-full" />
                </Link>
                <div className="mt-2.5">
                  <h3 className="line-clamp-1 font-display text-sm font-semibold text-ink">{book.title}</h3>
                  <p className="truncate text-xs text-subtle">{book.author}</p>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-line/70">
                      <div className="h-full rounded-full bg-gradient-to-r from-coral to-yellow" style={{ width: `${progress}%` }} />
                    </div>
                    <span className="text-[11px] font-medium text-subtle">{progress}%</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </section>
  );
}
